// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!

import type { TimeSignatureAlias } from 'shared/definitions/musicTheory/rhythms';
import type { InteractiveOptions, QuestionDataOptions } from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import * as Skills from '../uTheory.Skills/skills';
import utils from '../utils';
import config from '../config';
import RhythmChecker from './rhythmChecker';
import TablaInstrument from '../uTheory.Notation/tablaInstrument';
import Clef from '../uTheory.Notation/clef';
import Staff from '../uTheory.Notation/staff';
import TimeSignature from '../uTheory.Notation/timeSignature';
import Voice from '../uTheory.Notation/voice';
import KeySignature from '../uTheory.Notation/keySignature';
import Music from '../uTheory.Notation/music';
import MusicalClock from '../uTheory.Notation/musicalClock';
import Recorder from '../uTheory.Notation/recorder';
import Pointer from '../uTheory.Notation/pointer';
import errorProvider from '../errorProvider';
import { getCssProperty } from './getCssProperty';
import type { RhythmPerformanceButtons } from '../uTheory.Notation/letterPanel';

/* eslint-disable class-methods-use-this */

interface QuestionBankRhythm {
  id: string;
  attribution: string | null;
  difficulty: number;
  meter: TimeSignatureAlias;
  musicString: string;
  tempoRange: number[];
  createdAt: Date;
  updatedAt: Date;
}

interface RhythmReadingQuestion {
  rhythm: {
    musicString: string;
    tempoRange: number[];
    meter: TimeSignatureAlias;
    skillId: string;
    topicId: string;
  };
  id: string | null;
  // topic: number;
  // skills: {
  //   [skillId: string]: number;
  // };
}

interface RhythmReadingOptions extends InteractiveOptions {
  /* as rhythm string */
  noInstructions?: boolean;
  /* when true, do not pull questionData from database, use options.questions */
  manualQuestions?: boolean;
}

interface PerformanceInstruction {
  text?: string;
  duration: number;
  buttonName?: keyof RhythmPerformanceButtons;
  callback?: Function;
}

let tablas;

class RhythmReading extends InteractivePrototype {
  declare options: RhythmReadingOptions;

  rhythmCheck: RhythmChecker;

  practiceAttempts: number;

  /* string skillIds */
  skills: string[];

  /* topicIds */
  topics: number[];

  meter: TimeSignature;

  voice: Voice;

  clef: Clef;

  mc: MusicalClock;

  declare questions: RhythmReadingQuestion[];

  declare currentQuestion: RhythmReadingQuestion;

  declare questionData: QuestionBankRhythm[];

  recorder: Recorder;

  /* when a user has pressed start */
  startRequested: boolean;

  /* whether the computer should play the rhythm for a user */
  practiceMode: boolean = false;

  timeoutIds: number[];

  /* if we're transitioning out of the entire interactive */
  exited: boolean = false;

  /* number of measures to count off */
  countoffMeasures: number;

  stopPointer: Pointer;

  recordingPointer: Pointer;

  metronomeDiv: HTMLDivElement;

  restartDiv: HTMLDivElement;

  rhythmThreshold: number;

  constructor(displayDiv: HTMLDivElement, options: RhythmReadingOptions, userData) {
    super(displayDiv, options, userData);
    if (!tablas) tablas = new TablaInstrument();

    const sectionRhythmThreshold =
      this.userData?.userStore?.currentSection?.options?.performanceThresholds?.rhythm;
    const rhythmThreshold = Number.isFinite(sectionRhythmThreshold) ? sectionRhythmThreshold : 80;
    const accuracyAccommodation = Number.isFinite(this.userData?.accommodation?.accuracy)
      ? this.userData.accommodation.accuracy
      : 1;
    this.rhythmThreshold = rhythmThreshold * accuracyAccommodation;

    this.settings = {
      instructions: true,
      editor: false,
      notation: {
        scrolling: 2,
        ariaTitle: {
          theWordNote: false,
          keySignature: false,
          letterName: false,
          octave: false,
          measureNumber: false,
        },
      },
      piano: false,
      letters: { rhythmPerformanceButtons: true },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
      uPrefs: { appName: 'rhythmReading', optional: false },
      questionData: {
        filename: 'rhythms',
        directory: `${config.apiUrl}/${config.questionsDirectory}`,
      },
      loadAudioFirst: true,
    };

    this.layout = {
      instructions: { percent: 10, maxPercent: 25 },
      notation: { percent: 40 },
      letters: { percent: 50 },
    };
    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: RhythmReadingOptions): Promise<any> {
    // Set our topics based on skills passed in drill options, provided
    // we're not in drill or testMode's
    if (!options.drill || options.testMode) {
      delete this.settings.questionData;
    } else {
      this.topics = this.setTopics(options.skills);
    }

    this.settings.checkMark = displayDiv;

    // now call the prototype's init function:
    return super.init(displayDiv, options).then(() => {
      this.rhythmCheck.setRenderer(this.notation);
      if (this.options.noInstructions) {
        this.player.buffer.enableRecording();
      }

      // How many practice attempts have they had this session?
      this.practiceAttempts = 0;
    });
  }

  createCustomDivs() {
    const metronomeDiv = document.createElement('div');
    metronomeDiv.id = 'metronome-div';
    metronomeDiv.style.position = 'absolute';
    metronomeDiv.style.width = '100%';
    metronomeDiv.style.height = '1em';
    metronomeDiv.style.opacity = '0';
    // metronomeDiv.style.fontSize = '24px';
    // metronomeDiv.style.margin = '0 12px 12px 0';
    // metronomeDiv.style.padding = '12px 15px 15px 12px';
    // metronomeDiv.style.borderRadius = '0 0 12px 0';
    metronomeDiv.style.backgroundColor =
      getCssProperty('--blue-200-as-dark-bg') || config.Colors.blueGrey;
    metronomeDiv.style.color = 'white';
    metronomeDiv.style.transition = 'background-color 0.3s ease-in';
    this.metronomeDiv = metronomeDiv;
    this.displayDiv.insertBefore(metronomeDiv, this.instructionsDiv);

    const restartDiv = document.createElement('div');
    restartDiv.id = 'restart-div';
    restartDiv.style.position = 'relative';
    restartDiv.style.float = 'right';
    restartDiv.style.opacity = '0';
    restartDiv.style.display = 'none';

    const restartButton = document.createElement('button');
    restartButton.style.fontSize = '3vw';
    restartButton.style.fontWeight = 'bold';
    restartButton.style.padding = '1vw 1vw';
    restartButton.style.borderRadius = '1vw 1vw';
    restartButton.style.backgroundColor = getCssProperty('--button-color') || config.Colors.orange;
    restartButton.style.color = 'white';
    const reset = String.fromCharCode(8634);
    restartButton.innerHTML = `
      <span style="display: inline-block; transform: rotate(-80deg);">${reset}</span>
`;

    restartDiv.appendChild(restartButton);
    restartDiv.style.transition = 'background-color 0.3s ease-in';
    restartDiv.addEventListener('click', () => this.restartRhythm());
    this.restartDiv = restartDiv;
    // this.displayDiv.parentElement.insertBefore(restartButton, this.displayDiv);
    this.displayDiv.insertBefore(restartDiv, this.instructionsDiv);
  }

  setTopics(skills: (string | number)[]): number[] {
    if (!skills) {
      this.skills = [];
      return Skills.getTree()[1].topics.map((topic) => topic.id);
    }
    skills = skills.map(Skills.makeString).filter((skill) => skill);
    this.skills = skills as string[];

    let topics = skills
      .map((skill) => {
        const skillObj = Skills.getSkill(skill);
        if (skillObj) return skillObj.topicId;
        return false;
      })
      .filter((topicId) => !!topicId);
    topics = utils.unique(topics);

    (this.settings.questionData as QuestionDataOptions).filename = `rhythms?topicIds=${topics.join(
      '&topicIds=',
    )}`;
    return topics as number[];
  }

  initializeMusic() {
    // Create holder music file w/ empty percussion staff:
    const clef = new Clef('percussion');
    const staff = new Staff(clef);

    this.meter = new TimeSignature('4/4');

    // gcr... you deleted new Voice(this.measure) because measure's not initialized anywhere
    const voice = new Voice();
    voice.setStaff(staff);
    this.voice = voice;

    const keySig = new KeySignature('C', 'Major');

    this.music = new Music(staff, voice, keySig);
    this.music.setTimeSignature(this.meter);
    this.clef = clef;

    this.initializeRhythmChecker();
  }

  initializeElements() {
    super.initializeElements();
    if (!this.layout.notation?.div) {
      throw Error('Cannot add event listener to notation when div has not yet been created.');
    }
    if (!this.letterPanel?.rhythmPerformanceButtons) {
      throw Error('Cannot add event listener when rhythm perf buttons not created.');
    }
    const handleTapDownEvent = (evt: MouseEvent | TouchEvent) => {
      // Is the tap button visible? If not, get out of here.
      if (this.letterPanel?.rhythmPerformanceButtons?.tap[0].style.opacity !== '1') {
        return;
      }
      evt.preventDefault();
      evt.stopPropagation();
      this.buttonHandler('noteOn');
    };

    const handleTapUpEvent = (evt: MouseEvent | TouchEvent) => {
      // Is the tap button visible? If not, get out of here.
      if (this.letterPanel?.rhythmPerformanceButtons?.tap[0].style.opacity !== '1') {
        return;
      }
      evt.preventDefault();
      evt.stopPropagation();
      this.buttonHandler('noteOff');
    };

    this.layout.notation.div.addEventListener('mousedown', handleTapDownEvent);
    this.layout.notation.div.addEventListener('touchstart', handleTapDownEvent);
    this.layout.notation.div.addEventListener('mouseup', handleTapUpEvent);
    this.layout.notation.div.addEventListener('touchend', handleTapUpEvent);
  }

  newQuestion() {
    this.updateInstructions('Play the rhythm below.');
    this.setRestartButtonVisibility(false);
    const q = this.questions[this.questionNumber];
    this.currentQuestion = q;
    this.music = Music.fromString(q.rhythm.musicString);
    this.voice = this.music.voices[0];
    this.voice.setInstrument(tablas);

    this.notation.music = this.music;

    this.meter.set(q.rhythm.meter);
    this.mc.setMeter(this.meter);

    this.setTempo(q);
    this.music.tempo = this.tempo;
    this.notation.render();
    this.notation.resetScroll();
    this.notation?.enableUserScrolling(false);
    super.newQuestion();
    this.resize();
  }

  resize() {
    if (this.metronomeDiv) {
      this.metronomeDiv.style.width = `${this.displayDiv.clientWidth}px`;
    }
    return super.resize();
  }

  nextQuestion() {
    if (++this.questionNumber < this.questions.length) {
      this.letterPanel.switchRhythmView('start');
      this.currentQuestion = this.questions[this.questionNumber];
      this.newQuestion();
    } else {
      this.gameOver();
    }
  }

  setTempo(q: RhythmReadingQuestion) {
    let tempoRange = q.rhythm.tempoRange;
    if (q.rhythm.topicId) {
      const topic = Skills.getTopic(q.rhythm.topicId);
      tempoRange = topic ? topic.tempo : tempoRange;
    }
    this.tempo = tempoRange[0] + Math.random() * (tempoRange[1] - tempoRange[0]);
    this.mc.setTempo(this.tempo);
  }

  initializeRhythmChecker() {
    const clockOptions = {
      meter: this.meter,
      tempo: 100,
      click: true,
    };

    const recorderOptions = {
      quantUnit: '16',
      spaceBar: true,
      midi: true,
    };

    this.mc = new MusicalClock(clockOptions);
    this.mc.addMetronomeCallback(() => {
      if (this.mc.getPointer().isLessThan(this.recordingPointer)) return;
      // if (beat === 0) {
      //   const fontSize = window.getComputedStyle(this.instructionsDiv, null).fontSize;
      //   this.metronomeDiv.style.fontSize = fontSize;
      // }
      const flashRate = Math.floor((this.mc.beatSec * 10) / 1.5) / 10;
      // this.metronomeDiv.innerHTML = `${beat + 1}`;
      this.metronomeDiv.style.transition = 'none';
      this.metronomeDiv.style.opacity = '1';
      this.waitForDomUpdate();
      this.metronomeDiv.style.transition = `opacity ${flashRate}s ease-in`;
      this.metronomeDiv.style.opacity = '0';
    });
    this.recorder = new Recorder(this.mc, recorderOptions);
    this.rhythmCheck = new RhythmChecker(this.mc, {
      playSounds: true,
    });
  }

  addListeners() {
    this.letterPanel.addCustomButtonCallback(this.buttonHandler, this);
  }

  buttonHandler(
    buttonName: 'start' | 'next' | 'startPractice' | 'start countoff' | 'noteOn' | 'noteOff',
  ) {
    const rpb = this.letterPanel.rhythmPerformanceButtons;
    switch (buttonName) {
      case 'start':
        this.startPerformance();
        break;
      case 'next':
        this.nextQuestion();
        break;
      case 'startPractice':
        this.startPractice();
        break;
      case 'start countoff':
        this.countOff();
        this.setupScrolling();
        this.mc.play();
        rpb.start.concat(rpb.repeat, rpb.done).forEach((button) => {
          button.style.transition = 'none';
          button.style.opacity = '1';
        });
        this.letterPanel.rhythmPerformanceButtons.startCountoff[0].disabled = false;
        break;
      case 'noteOn':
        this.recorder.noteOn();
        break;
      case 'noteOff':
        this.recorder.noteOff();
        break;
    }
  }

  generateQuestions(questionsArray?): RhythmReadingQuestion[] {
    questionsArray = questionsArray || this.options.questions;
    this.questionPrompt = 'Play the rhythm below.';

    if (this.options.manualQuestions) {
      this.questions = this.generateManualQuestions(questionsArray);
      return undefined;
    }

    return this.generateDrillQuestions();
  }

  generateDrillQuestions(): RhythmReadingQuestion[] {
    const skills = this.skills || [];
    const uPrefs = this.uPrefs;

    function countSkills(a) {
      return Object.getOwnPropertyNames(a.skills).reduce((tally, skill) => {
        if (skills.indexOf(skill) !== -1) {
          return tally + a[skill];
        }
        return tally;
      }, 0);
    }

    // All of the questions available in this.questionData will be within
    // topic, because we set the topics earlier. Now, we just need to do
    // two things:
    //  #1: Shuffle, to get some randomness
    //  #2: Sort by most frequently performed, to prevent repetition,
    //      then by ones that train our skills the highest # of times.
    // utils.shuffle(this.questionData);
    // utils.sort(
    //   this.questionData,
    //   (a, b) => {
    //     // sort by least performed
    //     const aHist = uPrefs.getQuestionHistory(a.id);
    //     const bHist = uPrefs.getQuestionHistory(b.id);
    //     const aCount = aHist ? aHist.attempts : 0;
    //     const bCount = bHist ? bHist.attempts : 0;
    //     // return -1 to keep a first, +1 to swap, 0 if equal:
    //     return aCount - bCount;
    //   },
    //   (a, b) =>
    //     // then by targets most skills
    //     countSkills(b) - countSkills(a)
    // );

    const questionData = this.questionData.slice(0, this.options.maxQuestions || 4);

    // Put them in our questions format...
    this.questions = questionData.map((q) => ({
      rhythm: q,
      id: q.id,
      // topic: q.topicId,
      // skills: q.skills,
    }));
    return this.questions;
  }

  generateManualQuestions(
    manualQuestons: string[] = this.options.questions,
  ): RhythmReadingQuestion[] {
    // For each manual question
    return manualQuestons.map((question) => {
      // Make it into a proper musicString
      const rhythm = this.manualQuestionStringToRhythm(question);
      // Analyze it's rhythm for skills
      const music = Music.fromString(rhythm.musicString);
      const voice = music.voices[0];
      const skills = this.calculateSkills(voice);
      // And make it into a proper question object
      return {
        rhythm,
        id: null,
        topic: skills.topic,
        skills: skills.skills,
      };
    });
  }

  /**
   * Turn a simplified rhythmString into a musicString object
   *
   * @param {string} rhythmString in format such as:
   *    "12/8: tempo=62 q. q. h. | h. e e e e e e | h. h.r",
   *  or "6/8: e e e q. | e e e q. | q. q. | e e e q."
   * @returns {object} { tempoRange: [low, high], musicString, meterString }
   */
  manualQuestionStringToRhythm(rhythmString: string): {
    musicString: string;
    tempoRange: number[];
    meter: TimeSignatureAlias;
  } {
    const music = Music.fromRhythmString(rhythmString, { flagPickup: true });
    const tempo = music.tempo || 80;
    const meter = music.getTimeSignature()!.toString();
    const musicString = music.toString(true);

    return {
      musicString,
      tempoRange: [tempo, tempo],
      meter,
    };
  }

  calculateSkills(voice: Voice) {
    const skills = Skills.processRhythm(voice);
    const skillsCount: { [skillId: string]: number } = {};
    let topicId = -1;
    skills.forEach((skill) => {
      if (skill.hasOwnProperty('topicId')) {
        topicId = Math.max(topicId, skill.topicId);
      }
      if (skillsCount.hasOwnProperty(skill.textId)) {
        skillsCount[skill.textId]++;
      } else {
        skillsCount[skill.textId] = 1;
      }
    });

    return {
      topic: topicId,
      skills: skillsCount,
    };
  }

  makeTextNode(text: string): void {
    const n = document.createElement('textNode');
    n.innerHTML = text;
    n.style.color = '#B4B4B4';
    n.style.margin = '0px 10px';
    this.instructionsDiv.appendChild(n);
  }

  startPerformance() {
    if (this.startRequested) return;
    this.startRequested = true;
    // This is a kluge for iOS, to enable sound playback. If the first request
    // for sound playback happens in a promise, instead of as a direct response
    // to a user input, the playback context fails.
    this.mc.player.play('silence-2');
    // const experienceType = this.practiceMode ? 'practiceExperience' : 'instructionExperience';
    // const experience = this.uPrefs.getPreference(experienceType) || 0;
    // let delay = experience < 3 ? 4000 : 1500;
    // delay = experience < 10 ? delay : 1000;
    // Fade start button(s)...
    const rpb = this.letterPanel.rhythmPerformanceButtons;
    const startButtons = rpb.start.concat(rpb.repeat).concat(rpb.done);
    const timeoutIds = [];
    startButtons.forEach((button) => {
      button.style.transition = 'opacity 0.5s ease-in';
      button.style.opacity = '0';
      // timeoutIds.push(
      //   window.setTimeout(() => {
      //     button.style.transition = 'none';
      //     button.style.opacity = 1;
      //   }, delay)
      // );
    });

    // Track timeouts to cancel if exited:
    this.timeoutIds = timeoutIds;

    // this.letterPanel.switchRhythmView("tap");

    const mc = this.mc;
    const rc = this.rhythmCheck;

    // Stop anything previous...
    rc.reset();
    this.notation.render();
    mc.stop();
    this.recorder.stopCallbacks = [];
    this.recorder.realTimeCallbacks = [];

    // if we're not in demo mode, check how we're doing -- otherwise, see countOff()
    if (!this.options.noInstructions) {
      this.recorder.addRealTimeCallback(rc.checkEvent, rc);
    }

    // Setup for this question...
    rc.setVoice(this.music.voices[0]);

    // Scroll to 0, in case we're repeating an exercise:
    this.notation.setScrollRate(0);
    this.notation.scrollTo(0);

    // Display the instructions, then start it all:
    this.displayPlayInstructions()
      .catch((e) => {
        console.log('Error displaying instructions:');
        console.log(e);
        console.log(e.stack);
        errorProvider.error(e);
        return Promise.resolve();
      })
      .then(() => {
        if (this.exited) return;
        // Start audio recording if we're in lesson recording mode
        if (this.options.noInstructions) {
          this.player.buffer.startRecording();
        }
        // Setup the visual countoff:
        // this.countOff();

        // And setup the scrolling of the rhythm:
        // this.setupScrolling();

        // window.mc = mc;

        // mc.play();
      })
      .catch((e) => {
        console.log('Error counting off and starting clock.');
        console.log(e);
        console.log(e.stack);
        errorProvider.error('Error counting off and starting clock.', e);
      });
  }

  displayPlayInstructions(): Promise<HTMLSpanElement | void> {
    if (this.options.noInstructions) {
      const rpb = this.letterPanel.rhythmPerformanceButtons;
      this.countOff();
      this.setupScrolling();
      this.mc.play();
      rpb.start.concat(rpb.repeat, rpb.done).forEach((button) => {
        button.style.transition = 'none';
        button.style.opacity = '1';
      });
      this.letterPanel.rhythmPerformanceButtons.startCountoff[0].disabled = false;
      return Promise.resolve();
    }

    // GCR KLUGE:
    this.letterPanel.rhythmPerformanceButtons.tap[0].style.transition = 'opacity 0.5s ease-in';

    const instructionsList = this.makeInstructionsList();
    const updateInstructions = this.updateInstructions.bind(this);

    const timeoutIds = this.timeoutIds;
    const makeNode = function (text): HTMLSpanElement {
      const span = document.createElement('span');
      const node = document.createTextNode(text);
      span.style.opacity = '0';
      span.style.color = '#00000';
      span.style.margin = '0px 10px';
      span.style.transition = 'opacity 0.5s ease-in';
      span.appendChild(node);
      return span;
    };

    const wait = (seconds) =>
      new Promise<void>((resolve) => {
        const id = window.setTimeout(() => {
          resolve();
        }, seconds * 1000);
        timeoutIds.push(id);
      });

    const addInstruction = (
      { text, duration, buttonName, callback }: PerformanceInstruction,
      isFinal: boolean,
    ): Promise<unknown | HTMLSpanElement> => {
      if (callback) {
        callback();
      }
      let node: HTMLSpanElement;
      if (text) {
        node = makeNode(text);
        updateInstructions(node);
        node.style.opacity = '1';
      }
      if (buttonName) {
        this.fadeButton(buttonName, 'in');
      }
      if (isFinal) return Promise.resolve(node);
      return wait(0.5 + duration).then(() => {
        node.style.opacity = '0';
        return wait(0.5);
      });
    };

    let promise: Promise<any> = Promise.resolve();
    instructionsList.forEach((instruction, index) => {
      const isFinal = index === instructionsList.length - 1;
      promise = promise.then(() => addInstruction(instruction, isFinal));
    });
    return promise;
  }

  private fadeButton(
    buttonName: keyof RhythmPerformanceButtons,
    inOrOut: 'in' | 'out',
    duration: number = 0.5,
  ): HTMLButtonElement {
    const button = this.letterPanel.rhythmPerformanceButtons[buttonName][0] as HTMLButtonElement;
    if (inOrOut === 'in') {
      button.style.transition = 'opacity 0s';
      button.style.opacity = '0';
      this.letterPanel.switchRhythmView(buttonName);
    }
    this.waitForDomUpdate();
    button.style.transition = `opacity ${duration}s ease-in`;
    button.style.opacity = inOrOut === 'in' ? '1' : '0';
    return button;
  }

  makeInstructionsList(): PerformanceInstruction[] {
    const experience = this.uPrefs?.getPreference('instructionExperience') ?? 0;
    // const experience = 20;
    const withinHours = this.uPrefs?.withinHours('bluetoothOkDate', 72) ?? true;
    // const withinHours = true;
    const instructionsList: PerformanceInstruction[] = [];
    if (!withinHours) {
      instructionsList.push({
        text: 'Note: bluetooth headphones may have a delay. Please use wired headphones or speakers.',
        duration: 4,
      });
    }

    if (this.practiceMode) {
      const practiceExperience = this.uPrefs.getPreference('practiceExperience') ?? 0;
      if (practiceExperience < 5 && this.practiceAttempts < 1) {
        this.practiceAttempts++;
        instructionsList.push({
          text: 'In practice mode, the computer makes the sounds for you.',
          duration: 3,
        });
        instructionsList.push({
          text: 'You should try and play right with the drum sound.',
          duration: 3,
        });
      }
    }
    // If they've done this lots, we just give them "Play after x beats":
    const beats = this.calculateCountOff().beats;
    const countOffTextDuration = experience < 10 ? (experience < 5 ? 3 : 1.75) : 1.25;

    instructionsList.push({
      text: `Play after ${beats} metronome clicks.`,
      duration: countOffTextDuration,
    });

    // If they've never done it, we also give them: "Tap the button to play."
    if (experience < 2 && !this.uPrefs.withinHours('lastPerformanceDate', 1)) {
      instructionsList.push({
        text: utils.isTouch()
          ? 'Use the "tap" button to play the notes.'
          : 'Click the button or use spacebar to play notes.',
        duration: 3,
      });
    }

    // "Here we go..."
    instructionsList.push({
      callback: () => this.drawCountoff(),
      duration: 1,
      buttonName: 'startCountoff',
    });

    return instructionsList;
  }

  startPractice() {
    this.practiceMode = true;
    const tempo = this.tempo * 0.66;
    // tempo = Math.max(tempo || this.currentQuestion.tempoRange[0]);
    this.mc.setTempo(tempo);
    this.startPerformance();
  }

  disablePracticeMode() {
    this.practiceMode = false;
    this.mc.setTempo(this.tempo);
  }

  calculateCountOff(): { measures: number; beats: number } {
    const countoffMeasures = this.mc.meter.beatsPerMeasure <= 2 ? 2 : 1;
    const countoffBeats = this.mc.meter.beatsPerMeasure * countoffMeasures;
    return { measures: countoffMeasures, beats: countoffBeats };
  }

  countOff() {
    const startCountoffButton = this.letterPanel.rhythmPerformanceButtons
      .startCountoff[0] as HTMLButtonElement;
    startCountoffButton.disabled = true;
    // startCountoffButton.style.opacity = '0';

    if (!this.music) {
      throw Error('Cannot count off when music is not loaded.');
    }
    if (!this.tempo) {
      throw Error('Cannot count off when tempo is not set.')
    }
    const p = new Pointer('0:1:0', this.meter);
    const beatDurationSeconds = p.toSeconds(this.tempo);
    const fadeSpeed = Math.min(0.5, beatDurationSeconds / 2);
    this.fadeButton('startCountoff', 'out', fadeSpeed);
    utils.wait(fadeSpeed).then(() => {
      this.fadeButton('tap', 'in', fadeSpeed);
    });

    this.rhythmCheck.setSounds(!this.practiceMode && !this.options.noInstructions);
    // If there are 2 or fewer beats in the measure, give us
    // four beats of countoff. Otherwise, just give us one measure:
    const countOff = this.calculateCountOff();
    // Is there a pickup? If so, start recording a measure earlier...
    const recordingMeasure = this.music.isPickup ? countOff.measures - 1 : countOff.measures;

    console.log('recordingMeasure', recordingMeasure);
    const recordingPointer = new Pointer(`${recordingMeasure}:0`, this.mc.meter);

    // Figure out when to stop:
    const duration = this.voice.getNumberOfMeasures();
    const stopPointer = recordingPointer.clone().add(`${duration}:0:1`);

    this.mc.onTime({ startPointer: `${countOff.measures}:0:0` }, () => {
      if (!this.practiceMode) {
        this.setRestartButtonVisibility(true);
      }
    });

    if (this.practiceMode || this.options.noInstructions) {
      this.voice.setInstrument(tablas);
      this.mc.queue({
        startPointer: recordingPointer,
        sound: this.voice,
      });
      this.mc.onTime(
        {
          startPointer: recordingPointer,
        },
        () => {
          console.log('recording!');
        },
      );
    }

    if (this.options.noInstructions) {
      this.music.voices[0]?.getPerformancePointers().forEach((pointer) => {
        this.mc.onTime(
          {
            startPointer: pointer.startPointer.clone().add(recordingPointer),
          },
          () => {
            this.rhythmCheck.checkEvent({
              performanceStartTime: this.mc.getSeconds(),
              performanceStartPointer: pointer.startPointer,
              midiNumber: 60,
            });
          },
        );
      });
    }

    this.mc.startMetronome();
    this.mc.recordAt(
      {
        startPointer: recordingPointer,
        stopPointer,
      },
      this.recorder,
    );

    this.mc.stopMetronome({
      startPointer: stopPointer.clone().subtract('0:1:0.2'),
    });

    this.mc.onTime({ startPointer: stopPointer.clone() }, this.stop.bind(this));
    this.mc.onTime({ startPointer: stopPointer.clone() }, () => {
      this.startRequested = false;
    });

    this.countoffMeasures = countOff.measures;
    this.stopPointer = stopPointer;
    this.recordingPointer = recordingPointer;
  }

  drawCountoff() {
    // Clear the instructionsDiv:
    this.instructionsDiv.innerHTML = '';

    // Create & add text nodes for it:
    let texts;
    let countoffMeasures;
    const bpm = this.mc.meter.beatsPerMeasure;
    switch (bpm) {
      case 6:
        texts = ['1', '2', '3', '4', 'Ready', 'Go!'];
        countoffMeasures = 1;
        break;
      case 5:
        texts = ['1', '2', '3', 'Ready', 'Go!'];
        countoffMeasures = 1;
        break;
      case 4:
        texts = ['1', '2', 'Ready', 'Go!'];
        countoffMeasures = 1;
        break;
      case 3:
        texts = ['1', '2', 'Go!'];
        countoffMeasures = 1;
        break;
      case 2:
        texts = ['1', '2', 'Ready', 'Go!'];
        countoffMeasures = 2;
        break;
    }

    const makeNode = (text): HTMLSpanElement => {
      const span = document.createElement('span');
      const node = document.createTextNode(text);
      span.style.opacity = '0';
      span.style.color = '#B4B4B4';
      span.style.margin = '0px 10px';
      span.style.transition = 'opacity 0.5s ease-in';
      span.appendChild(node);
      return span;
    };

    const textNodes: HTMLSpanElement[] = texts.map(makeNode);

    const meter = this.mc.meter;
    textNodes.forEach((node, index) => {
      // GCR To-Do: Queue up the flash of all of them with MC
      const flashPointer = new Pointer(`0:${index}`, meter);
      flashPointer.simplify();
      this.mc.onTime({ startPointer: flashPointer }, this.flashCountoff, this, node);
      this.instructionsDiv.appendChild(node);
      this.waitForDomUpdate();
      node.style.opacity = '1';
    }, this);

    // If practice mode, display an indication like "Practice mode:"
    if (this.practiceMode) {
      const node = makeNode('...practice mode...');
      node.style.opacity = '0';
      this.mc.onTime(
        { startPointer: `${countoffMeasures}:0:1` },
        () => {
          this.updateInstructions(node);
          this.fadeNode(node, true);
        },
        this,
      );
    }
  }

  private waitForDomUpdate() {
    // @ts-ignore unused variable because it forces a dom calculation to happen.
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const doNotDeleteMe = this.instructionsDiv.clientWidth; // DO NOT DELETE THIS LINE!!!
  }

  private scrollCancel?: () => void;

  setupScrolling() {
    const mc = this.mc;
    const renderer = this.notation;
    renderer.render();

    const measures = this.voice.getNumberOfMeasures();
    // How long should scrolling one measure take?
    const duration = mc.getSeconds('1:0:0') * measures;
    const { measures: countOffMeasures } = this.calculateCountOff();
    const scrollMeasure = this.music.isPickup ? countOffMeasures - 1 : countOffMeasures;
    this.music.tempo = this.tempo;
    console.log({ countOffMeasures, scrollMeasure });
    mc.onTime({ startPointer: `${scrollMeasure}:0:0` }, () => {
      this.scrollCancel = renderer.animate(duration);
    });

    // renderer.setScrollRate(scrollRate);

    // const scroll = function(startPointer, targetMeasure) {
    //   mc.onTime({ startPointer }, renderer.scrollTo, renderer, targetMeasure);
    // };
    // // duration + 1 because of countoff measure... may have to adjust
    // // if counting off two measures in future...
    // let m = 1;
    // const scrollPointer = this.recordingPointer.getNextMeasure();

    // while (m <= duration) {
    //   scroll(scrollPointer.clone(), m);
    //   scrollPointer.add('1:0');
    //   m++;
    // }
  }

  flashCountoff(node) {
    node.style.color = '#1AAC64';
    this.fadeNode(node);
  }

  fadeNode(node: HTMLElement, fadeIn: boolean = false) {
    const opacity = fadeIn ? '1' : '0';
    // the next line is here to force a dom update, otherwise the node does not flash.
    this.waitForDomUpdate();
    node.style.transition = 'opacity 1s ease-in, color 1s ease-in';
    if (!fadeIn) node.style.color = '#B4B4B4';
    node.style.opacity = opacity;
  }

  private setRestartButtonVisibility(visible: boolean) {
    if (!visible) {
      this.restartDiv.style.transition = 'none';
      this.restartDiv.style.opacity = '0';
      this.restartDiv.style.display = 'hidden';
      this.restartDiv.style.position = 'absolute';
      (this.restartDiv.firstElementChild as HTMLButtonElement).disabled = true;
    } else {
      (this.restartDiv.firstElementChild as HTMLButtonElement).disabled = false;
      this.restartDiv.style.width = 'auto';
      this.restartDiv.style.display = 'inline-block';
      this.restartDiv.style.position = 'absolute';
      this.waitForDomUpdate();
      this.restartDiv.style.transition = 'opacity 2.5s ease-in';
      this.restartDiv.style.opacity = '1';
    }
  }

  private restartRhythm() {
    this.mc.stop();
    this.recorder.stop();
    if (this.scrollCancel) this.scrollCancel();

    this.displayDiv.style.transition = 'none';
    this.displayDiv.style.opacity = '0';
    this.waitForDomUpdate();
    this.letterPanel.switchRhythmView('start');
    this.startRequested = false;
    this.displayDiv.style.transition = 'opacity 0.5s ease-in';
    this.displayDiv.style.opacity = '1';

    this.newQuestion();
  }

  stop() {
    if (this.options.noInstructions) {
      this.player.buffer.stopRecording();
    }
    this.setRestartButtonVisibility(false);
    this.notation.enableUserScrolling(true);
    const average = this.rhythmCheck.stop();

    let experienceType;
    let experience;
    const prefs: { [key: string]: any } = {};
    prefs.lastPerformanceDate = new Date();

    if (average.average > this.rhythmThreshold) {
      experienceType = this.practiceMode ? 'practiceExperience' : 'instructionExperience';
      experience = this.uPrefs.getPreference(experienceType) || 0;
      experience++;
      prefs[experienceType] = experience;
      prefs.bluetoothOkDate = new Date();
    }

    if (!this.options.manualQuestions) {
      this.uPrefs.saveQuestionAndPrefs(
        {
          id: this.currentQuestion.id,
          percent: average.average,
          tempo: this.tempo,
        },
        prefs,
      );
    } else if (average.average > this.rhythmThreshold) {
      this.uPrefs.setPreferences(prefs);
    }

    // Save to skills tree, as well:
    if (!this.practiceMode) {
      this.progressTracker.rhythmAnswer(average.skills, false, true);
    }

    // If noInstructions we're in demo mode,
    // go on to the next if desired right away.
    if (this.options.noInstructions) {
      this.letterPanel.switchRhythmView('done');
      return;
    }

    // How'd they do? What's their next step?
    this.afterPerformanceDecisionTree(average.average);
  }

  afterPerformanceDecisionTree(average) {
    console.log('average', average);
    // If practicing, and average is 85% or higher, make them do it for real:
    if (this.practiceMode && average >= 85) {
      this.updateInstructions("Good, now let's do it for real!");
      this.letterPanel.switchRhythmView('start');
      this.disablePracticeMode();
      return;
    }

    // If practicing, and below 85%, give them option of more practice or
    // doing it for real:
    // Or, if for real, and average is < 80%:
    if (average < this.rhythmThreshold) {
      this.updateInstructions("Let's have another go at that one!");
      this.letterPanel.switchRhythmView('repeat');
      this.disablePracticeMode();
      return;
    }

    this.disablePracticeMode();
    let rewardText;
    // If average is > 80%, their choice:
    if (average > 97) rewardText = 'Great work!';
    if (average > 93) rewardText = 'Great work!';

    if (average > 97) rewardText = 'Great work!';
    else if (average > 93) rewardText = 'Nice work!';
    else if (average > 90) rewardText = 'Pretty good.';
    else if (average > 85) rewardText = 'Not bad.';
    else if (average >= 80) rewardText = 'Not too bad.';
    else rewardText = 'Maybe try again.';

    this.updateInstructions(`${rewardText} You were ${Math.ceil(average)}% accurate!`);
    this.setTempo(this.currentQuestion);
    this.letterPanel.switchRhythmView('done');
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion() {
    throw new Error('Checkpoint mode not yet supported for rhythmReading.');
    // eslint-disable-next-line
    return Promise.reject('Checkpoint mode not yet implemented.');
    // return this.loadingPromise.then(() => {
    //   this.question = questionString;
    //   const questionText = 'What is the name of the note below?';
    //   this.instructionsDiv.innerHTML = questionText;

    //   this.checkpointQuestion = {};
    //   return Promise.resolve(this.currentQuestion);
    // });
  }

  getAnswerAndData() {
    const correct = undefined;
    const skill = undefined;
    const answerAndData = {
      question: this.questions[this.questionNumber].rhythm.musicString,
      questionType: 'RhythmReading',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: null,
      studentAnswerSVG: null,
      correctAnswerText: null,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: skill,
    };
    return answerAndData;
  }

  exit() {
    if (this.timeoutIds) {
      this.timeoutIds.forEach((id) => {
        window.clearTimeout(id);
      });
    }
    this.exited = true;

    //      this.rhythmCheck.reset();
    if (this.mc) this.mc.stop();
    super.exit();
  }

  static registry = {
    description: 'Perform Rhythms',
    get skills() {
      this._skills =
        this._skills || Skills.skills.filter((s) => s.textId[1] === ':').map((s) => s.textId);
      return this._skills;
    },
    set skills(_skills) {
      this._skills = _skills;
    },
  };
}

export default RhythmReading;
